/**
 * Checkout identity false-negative helpers.
 * A missed author fetch is not "please verify" — retry, then ask them to try again.
 */

import { bothPartiesIdentityVerified, isIdentityRequiredError } from './identity';

export const VERIFICATION_UNCONFIRMED_CODE = 'verification_unconfirmed';
export const VERIFICATION_UNCONFIRMED_MESSAGE = 'Could not confirm verification, try again.';

/**
 * @param {Object} [error]
 * @returns {boolean}
 */
export const isVerificationUnconfirmedError = error =>
  error?.code === VERIFICATION_UNCONFIRMED_CODE ||
  error?.data?.code === VERIFICATION_UNCONFIRMED_CODE ||
  error?.statusText === VERIFICATION_UNCONFIRMED_CODE;

/**
 * @param {string} [message]
 * @returns {Error}
 */
export const verificationUnconfirmedError = (message = VERIFICATION_UNCONFIRMED_MESSAGE) => {
  const error = new Error(message);
  error.status = 503;
  error.code = VERIFICATION_UNCONFIRMED_CODE;
  error.statusText = VERIFICATION_UNCONFIRMED_CODE;
  error.data = { code: VERIFICATION_UNCONFIRMED_CODE, message };
  return error;
};

/**
 * Retry a real initiate when the server said identity_required but this
 * browser already sees both people verified, or when author fetch failed.
 *
 * @param {Object} [error]
 * @param {Object} [currentUser]
 * @param {Object} [listing]
 * @returns {boolean}
 */
export const shouldRetryIdentityRequiredAsFetch = (error, currentUser, listing) => {
  if (!error) {
    return false;
  }
  if (isVerificationUnconfirmedError(error)) {
    return true;
  }
  return (
    isIdentityRequiredError(error) && bothPartiesIdentityVerified(currentUser, listing?.author)
  );
};

/**
 * After one retry, keep a genuine unverified 403. Map a false identity
 * 403 (both parties already verified here) to the fetch/retry copy.
 *
 * @param {Object} error
 * @param {Object} [currentUser]
 * @param {Object} [listing]
 * @returns {Object}
 */
export const mapIdentityRequiredWhenBothVerified = (error, currentUser, listing) => {
  if (!error) {
    return error;
  }
  if (isVerificationUnconfirmedError(error)) {
    return verificationUnconfirmedError(error?.data?.message || VERIFICATION_UNCONFIRMED_MESSAGE);
  }
  if (isIdentityRequiredError(error) && bothPartiesIdentityVerified(currentUser, listing?.author)) {
    return verificationUnconfirmedError();
  }
  return error;
};

const CHECKOUT_FLIGHT_STORAGE = 'sl.checkoutFlight';
const CHECKOUT_FLIGHT_TTL_MS = 45000;

/**
 * Stable key for one saved-card Book a time attempt.
 *
 * @param {Object} [params]
 * @returns {string}
 */
export const checkoutFlightKey = ({ listingId, bookingStart, bookingEnd } = {}) => {
  const listing = listingId?.uuid || listingId || '';
  const start =
    bookingStart instanceof Date ? bookingStart.toISOString() : bookingStart || '';
  const end = bookingEnd instanceof Date ? bookingEnd.toISOString() : bookingEnd || '';
  return `${listing}:${start}:${end}`;
};

const readCheckoutFlight = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(CHECKOUT_FLIGHT_STORAGE);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

/**
 * True when this tab already has a saved-card initiate in flight for `key`.
 *
 * @param {string} [key]
 * @returns {boolean}
 */
export const hasSavedCardCheckoutFlight = key => {
  if (!key) {
    return false;
  }
  const prev = readCheckoutFlight();
  return !!(prev?.key === key && Date.now() - (prev.at || 0) < CHECKOUT_FLIGHT_TTL_MS);
};

/**
 * Mark a saved-card initiate so a Safari remount cannot start a second one.
 *
 * @param {string} [key]
 * @returns {boolean} false when a flight is already open
 */
export const beginSavedCardCheckoutFlight = key => {
  if (!key || typeof window === 'undefined' || !window.sessionStorage) {
    return true;
  }
  if (hasSavedCardCheckoutFlight(key)) {
    return false;
  }
  try {
    window.sessionStorage.setItem(
      CHECKOUT_FLIGHT_STORAGE,
      JSON.stringify({ key, at: Date.now() })
    );
  } catch (e) {
    // Private mode — still allow the tap.
  }
  return true;
};

/**
 * @param {string} [key]
 */
export const endSavedCardCheckoutFlight = key => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  try {
    const prev = readCheckoutFlight();
    if (!prev || !key || prev.key === key) {
      window.sessionStorage.removeItem(CHECKOUT_FLIGHT_STORAGE);
    }
  } catch (e) {
    // ignore
  }
};

/**
 * Saved-card talk sessions must not auto-initiate until speculate succeeds.
 *
 * @param {Object} params
 * @returns {boolean}
 */
export const shouldAutoRequestSavedCard = ({
  skipPayUi,
  alreadyRequested,
  submitting,
  stripeReady,
  processReady,
  payoutsBlocked,
  listingNotFound,
  isPaymentExpired,
  listingAcceptsPaid,
  initiateOrderError,
  confirmPaymentError,
  confirmCardPaymentError,
  speculateInProgress,
  speculatedTransaction,
  speculateError,
  flightKey,
  ignorePriorErrors = false,
} = {}) => {
  if (
    !skipPayUi ||
    alreadyRequested ||
    submitting ||
    !stripeReady ||
    !processReady ||
    payoutsBlocked ||
    listingNotFound ||
    isPaymentExpired ||
    !listingAcceptsPaid
  ) {
    return false;
  }
  if (!ignorePriorErrors && (initiateOrderError || confirmPaymentError || confirmCardPaymentError)) {
    return false;
  }
  if (speculateInProgress || speculateError || !speculatedTransaction?.id) {
    return false;
  }
  if (flightKey && hasSavedCardCheckoutFlight(flightKey)) {
    return false;
  }
  return true;
};

/** How long Pay may sit on a spinner before we show retry + the card form. */
export const CHECKOUT_PAY_WAIT_MS = 6000;

/**
 * After a hang or timeout, stop hiding the Stripe form.
 *
 * @param {Object} params
 * @param {boolean} [params.skipPayUi]
 * @param {boolean} [params.waitingForSavedCard]
 * @param {boolean} [params.timedOut]
 * @returns {boolean}
 */
export const shouldRevealCheckoutPayUi = ({
  skipPayUi,
  waitingForSavedCard,
  timedOut,
} = {}) => !!timedOut || (!skipPayUi && !waitingForSavedCard);

/**
 * Reject if `work` does not settle in `ms`. Used so a hung Sharetribe
 * or Stripe promise cannot leave checkout spinning.
 *
 * @param {Promise} work
 * @param {number} [ms]
 * @param {string} [label]
 * @returns {Promise}
 */
export const withCheckoutPayTimeout = (work, ms = CHECKOUT_PAY_WAIT_MS, label = 'timeout') => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const error = new Error(label);
      error.code = 'checkout_pay_timeout';
      error.status = 504;
      reject(error);
    }, ms);
  });
  return Promise.race([Promise.resolve(work), timeout]).finally(() => {
    if (timer) {
      clearTimeout(timer);
    }
  });
};
